import React from 'react';
import { Check, X } from 'lucide-react';
import { passwordChecks, passwordStrengthScore, PASSWORD_POLICY_MESSAGE } from '@/lib/passwordPolicy';

const STRENGTH_LABELS = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLORS = ['#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0a2540'];

/**
 * Live password requirements checklist + strength bar.
 * Pass the current password value as `value`. Optionally hide the bar with showBar={false}.
 */
export function PasswordRequirements({ value, showBar = true, className = '' }) {
  const checks = passwordChecks(value || '');
  const score = passwordStrengthScore(value || '');
  const pct = ((score + 1) / 5) * 100;
  const label = STRENGTH_LABELS[score];
  const color = STRENGTH_COLORS[score];

  return (
    <div
      className={`rounded-lg border bg-slate-50/60 px-3 py-2.5 text-xs ${className}`}
      data-testid="password-requirements"
      role="status"
      aria-live="polite"
    >
      <div className="font-medium text-slate-700 mb-1.5">{PASSWORD_POLICY_MESSAGE}</div>
      {showBar && value ? (
        <div className="mb-2">
          <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full rounded-full transition-[width]" style={{ width: `${pct}%`, background: color }} />
          </div>
          <div className="mt-1 text-[11px] font-medium" style={{ color }}>{label}</div>
        </div>
      ) : null}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
        {checks.map((c) => (
          <li key={c.key} className="flex items-center gap-1.5">
            {c.ok ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
            ) : (
              <X className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            )}
            <span className={c.ok ? 'text-emerald-700' : 'text-slate-500'}>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
